import React, {
    useState,
    useEffect,
    useRef,
    useMemo,
    useCallback,
    useId,
} from 'react';
import type { GtChartProps } from './GtChartTypes';
import {
    MARGIN_TOP, MARGIN_BOT, MARGIN_RIGHT, MIN_X_LBL_PX,
    ZOOM_FACTOR, MIN_ZOOM_WIN, DEFAULT_COLORS,
    computeTicks, fmtNum, smoothBezier, arcPath,
    _isDateLike, _autoFmtShort, _autoFmtFull,
    type ZoomRange,
} from './GtChartHelpers';
import { GtChartTooltip } from './GtChartTooltip';
import type { GtTooltipRow } from './GtChartTooltip';
import { GtChartLegend } from './GtChartLegend';
import type { GtLegendItem } from './GtChartLegend';

// ── GtChart ──────────────────────────────────────────────────────────────────
const GtChart: React.FC<GtChartProps> = ({
    type = 'bar',
    series,
    categories = [],
    height = 240,
    colors,
    isDark = false,
    animate = true,
    enableZoom = true,
    formatY,
    formatX,
    formatTooltipValue,
    formatTooltipTitle,
    showGrid = true,
    tickCount = 5,
    minY = 0,
    referenceLines,
    showLegend,
    legendPosition = 'bottom',
    strokeWidth = 2,
    fillOpacity = 0.18,
    barRadius = 4,
    barGroupPadding = 0.28,
    stacked = false,
    smooth = 0.3,
    dotSize,
    showDataLabels = false,
    barGradient = true,
    onDataPointClick,
    className = '',
    noDataLabel = 'No data',
    donutInnerRatio = 0.55,
    donutLabel,
}) => {
    const uid = useId();
    const rawId = uid.replace(/:/g, '');
    const clipId = `vms-clip-${rawId}`;

    const containerRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const [containerW, setContainerW] = useState(500);
    const [mounted, setMounted] = useState(false);
    const [zoom, setZoom] = useState<ZoomRange | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const dragRef = useRef<{ mouseX: number; zStart: number; zEnd: number } | null>(null);
    const touchRef = useRef<{
        type: 'pinch' | 'pan';
        dist?: number;
        midFrac?: number;
        touchX?: number;
        zStart: number;
        zEnd: number;
    } | null>(null);

    // ── Tooltip state ──────────────────────────────────────────────────────────
    const [tip, setTip] = useState<{
        visible: boolean;
        cx: number;
        cy: number;
        catIdx: number;
        pieSlice?: number;
    }>({ visible: false, cx: 0, cy: 0, catIdx: -1 });

    // ── Measure container ──────────────────────────────────────────────────────
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const update = () => {
            const w = el.getBoundingClientRect().width;
            if (w > 0) setContainerW(w);
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // ── Stable animation key (avoids inline .map().join() in dep array) ────────
    const seriesNamesKey = useMemo(() => series.map((s) => s.name).join('|'), [series]);

    // ── Entry animation trigger ────────────────────────────────────────────────
    useEffect(() => {
        if (!animate) {
            setMounted(true);
            return;
        }
        setMounted(false);
        let raf1: number, raf2: number;
        raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(() => setMounted(true));
        });
        return () => {
            cancelAnimationFrame(raf1);
            cancelAnimationFrame(raf2);
        };
        // Re-animate when series names change (new data set)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [animate, type, seriesNamesKey]);

    // ── Derived flags ──────────────────────────────────────────────────────────
    const isPie = type === 'pie' || type === 'donut';
    const isStacked = stacked || type === 'stacked';
    const isZoomed = zoom !== null;
    const zoomEnabled = enableZoom && !isPie;

    // ── Resolved colors ────────────────────────────────────────────────────────
    const resolvedColors = useMemo((): string[] => {
        const palette = colors?.length ? colors : DEFAULT_COLORS;
        return series.map((s, i) => s.color ?? palette[i % palette.length]);
    }, [series, colors]);

    // ── Empty check ────────────────────────────────────────────────────────────
    const hasData = useMemo(
        () => series.some((s) => s.data.some((v) => typeof v === 'number' && v !== 0)),
        [series],
    );

    // ── Legend ────────────────────────────────────────────────────────────────
    const showLeg = showLegend ?? (series.length > 1 || isPie);
    const legRight = showLeg && legendPosition === 'right';
    const legBottom = showLeg && legendPosition === 'bottom';
    const legTop = showLeg && legendPosition === 'top';
    const legendRW = legRight ? 108 : 0;

    // ── Y range ──────────────────────────────────────────────────────────────
    const n = useMemo(
        () => Math.max(categories.length, ...series.map((s) => s.data.length), 1),
        [categories, series],
    );

    const rawMaxY = useMemo(() => {
        if (isPie) return 1;
        if (isStacked) {
            let m = 0;
            for (let i = 0; i < n; i++) {
                const sum = series.reduce((acc, s) => acc + (s.data[i] ?? 0), 0);
                if (sum > m) m = sum;
            }
            return m;
        }
        return Math.max(...series.flatMap((s) => s.data.filter(Number.isFinite) as number[]), 0);
    }, [series, isStacked, isPie, n]);

    const { ticks, niceMax } = useMemo(() => computeTicks(Math.max(rawMaxY, minY), tickCount), [rawMaxY, minY, tickCount]);
    const yFmt = formatY ?? fmtNum;

    // ── Stacked top-series per column (memoized — O(n×series) but only recomputed on data change) ──
    const topSeriesPerColMemo = useMemo((): number[] | null => {
        if (!isStacked) return null;
        return Array.from({ length: n }, (_, ci) => {
            for (let k = series.length - 1; k >= 0; k--) {
                if ((series[k].data[ci] ?? 0) > 0) return k;
            }
            return -1;
        });
    }, [isStacked, n, series]);

    // ── Dynamic left margin (widest Y label × ~7px/char + padding) ─────────────
    const marginLeft = useMemo(() => {
        const longest = Math.max(...ticks.map(t => yFmt(t).length), 1);
        return Math.max(36, longest * 7 + 18);
    }, [ticks, yFmt]);

    // ── SVG / chart geometry ─────────────────────────────────────────────────
    const svgW = containerW - legendRW;
    const svgH = height;
    const chartW = Math.max(1, svgW - marginLeft - MARGIN_RIGHT);
    const chartH = Math.max(1, svgH - MARGIN_TOP - MARGIN_BOT);

    // ── Zoom (clamped) ────────────────────────────────────────────────────────
    const { start: zStart, end: zEnd } = useMemo((): ZoomRange => {
        if (!zoom) return { start: 0, end: n - 1 };
        return { start: Math.max(0, zoom.start), end: Math.min(n - 1, zoom.end) };
    }, [zoom, n]);
    const zWindow = Math.max(0, zEnd - zStart);

    // ── Scale functions (zoom-aware) ─────────────────────────────────────────
    const yToSvg = useCallback(
        (v: number) => MARGIN_TOP + chartH * (1 - Math.max(0, v) / niceMax),
        [chartH, niceMax],
    );
    // Left edge of band i
    const xToSvg = useCallback(
        (i: number) => marginLeft + ((i - zStart) / Math.max(zWindow + 1, 1)) * chartW,
        [marginLeft, chartW, zStart, zWindow],
    );
    // Width of one band in the zoom window
    const xBandW = chartW / Math.max(zWindow + 1, 1);
    // Center x of band i
    const xCenter = useCallback(
        (i: number) => xToSvg(i) + xBandW * 0.5,
        [xToSvg, xBandW],
    );
    // SVG x → nearest data index
    const svgXToIdx = useCallback(
        (svgX: number) => {
            const frac = (svgX - marginLeft) / chartW;
            return Math.max(0, Math.min(n - 1, Math.floor(zStart + frac * (zWindow + 1))));
        },
        [marginLeft, chartW, zStart, zWindow, n],
    );

    // ── X labels — auto-format dates when no formatX provided ────────────────
    const autoDateCats = useMemo(
        () => categories.length > 0 && categories.every((c) => _isDateLike(c)),
        [categories],
    );

    const xLabels = useMemo(() => {
        const base = categories.length > 0
            ? categories.map((c, i) => {
                if (formatX) return String(formatX(c, i));
                if (autoDateCats) return _autoFmtShort(c);
                return String(c);
            })
            : Array.from({ length: n }, (_, i) => String(i + 1));
        return base;
    }, [categories, n, formatX, autoDateCats]);

    const xLabelStep = useMemo(
        () => Math.max(1, Math.ceil(MIN_X_LBL_PX / xBandW)),
        [xBandW],
    );

    // Visible index range with 1-slot padding
    const viStart = Math.max(0, Math.floor(zStart) - 1);
    const viEnd = Math.min(n - 1, Math.ceil(zEnd) + 1);

    // ── Tooltip data ─────────────────────────────────────────────────────────
    const tooltipRows = useMemo((): GtTooltipRow[] => {
        if (!tip.visible) return [];
        if (isPie) {
            const si = tip.pieSlice ?? 0;
            return [{
                name: xLabels[si] ?? series[0]?.name ?? `Slice ${si + 1}`,
                value: series[0]?.data[si] ?? 0,
                color: resolvedColors[si % resolvedColors.length],
            }];
        }
        if (tip.catIdx < 0) return [];
        return series.map((s, i) => ({
            name: s.name,
            value: s.data[tip.catIdx] ?? 0,
            color: resolvedColors[i],
        }));
    }, [tip, series, resolvedColors, isPie, xLabels]);

    const tooltipCatLabel = useMemo((): string => {
        const catIdx = isPie ? (tip.pieSlice ?? 0) : tip.catIdx;
        const rawCat = categories[catIdx] ?? catIdx;
        if (formatTooltipTitle) return formatTooltipTitle(rawCat, catIdx);
        if (autoDateCats) return _autoFmtFull(rawCat);
        if (isPie) return xLabels[tip.pieSlice ?? 0] ?? `Slice ${(tip.pieSlice ?? 0) + 1}`;
        return xLabels[tip.catIdx] ?? String(tip.catIdx);
    }, [tip, isPie, categories, formatTooltipTitle, autoDateCats, xLabels]);

    // ── Legend items ──────────────────────────────────────────────────────────
    const legendItems = useMemo((): GtLegendItem[] => {
        if (isPie) {
            return (series[0]?.data ?? []).map((_, i) => ({
                name: xLabels[i] ?? `Slice ${i + 1}`,
                color: resolvedColors[i % resolvedColors.length],
                total: null,
            }));
        }
        return series.map((s, i) => ({
            name: s.name,
            color: resolvedColors[i],
            total: (s.data ?? []).reduce((acc, v) => acc + (v ?? 0), 0),
        }));
    }, [series, isPie, xLabels, resolvedColors]);

    // ── Non-passive wheel → zoom ─────────────────────────────────────────────
    useEffect(() => {
        const el = svgRef.current;
        if (!el || !zoomEnabled) return;
        const handler = (e: WheelEvent) => {
            e.preventDefault();
            e.stopPropagation();
            const rect = el.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const frac = (mouseX - marginLeft) / chartW;
            const cursor = zStart + frac * (zWindow + 1);
            const dir = e.deltaY > 0 ? 1 : -1;
            const factor = dir > 0 ? ZOOM_FACTOR : 1 / ZOOM_FACTOR;
            const newWin = Math.max(MIN_ZOOM_WIN, (zWindow + 1) * factor - 1);
            let ns = cursor - frac * (newWin + 1);
            let ne = ns + newWin;
            if (ns < 0) { ne -= ns; ns = 0; }
            if (ne > n - 1) { ns -= ne - (n - 1); ne = n - 1; }
            ns = Math.max(0, ns); ne = Math.min(n - 1, ne);
            if (newWin >= n - 1 && ns <= 0.01) { setZoom(null); return; }
            setZoom({ start: ns, end: ne });
        };
        el.addEventListener('wheel', handler, { passive: false });
        return () => el.removeEventListener('wheel', handler);
    }, [zoomEnabled, marginLeft, chartW, zStart, zWindow, n]);

    // ── Touch pinch-to-zoom / single-finger pan ────────────────────────────────
    useEffect(() => {
        const el = svgRef.current;
        if (!el || !zoomEnabled) return;

        function getTouchDist(touches: TouchList) {
            const dx = touches[1].clientX - touches[0].clientX;
            const dy = touches[1].clientY - touches[0].clientY;
            return Math.hypot(dx, dy);
        }

        function onTouchStart(e: TouchEvent) {
            e.preventDefault();
            const rect = el!.getBoundingClientRect();
            if (e.touches.length === 2) {
                const dist = getTouchDist(e.touches);
                const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left;
                const midFrac = (midX - marginLeft) / chartW;
                touchRef.current = { type: 'pinch', dist, midFrac, zStart, zEnd };
            } else if (e.touches.length === 1) {
                touchRef.current = { type: 'pan', touchX: e.touches[0].clientX, zStart, zEnd };
            }
        }

        function onTouchMove(e: TouchEvent) {
            e.preventDefault();
            if (!touchRef.current) return;
            const st = touchRef.current;
            if (st.type === 'pinch' && e.touches.length === 2) {
                const newDist = getTouchDist(e.touches);
                const factor = (st.dist ?? newDist) / newDist;
                const curWin = st.zEnd - st.zStart;
                const newWin = Math.max(MIN_ZOOM_WIN, (curWin + 1) * factor - 1);
                const cursor = st.zStart + (st.midFrac ?? 0.5) * (curWin + 1);
                let ns = cursor - (st.midFrac ?? 0.5) * (newWin + 1);
                let ne = ns + newWin;
                if (ns < 0) { ne -= ns; ns = 0; }
                if (ne > n - 1) { ns -= ne - (n - 1); ne = n - 1; }
                ns = Math.max(0, ns); ne = Math.min(n - 1, ne);
                if (newWin >= n - 1 && ns <= 0.01) { setZoom(null); return; }
                setZoom({ start: ns, end: ne });
            } else if (st.type === 'pan' && e.touches.length === 1) {
                const dx = e.touches[0].clientX - (st.touchX ?? e.touches[0].clientX);
                const dIdx = (dx / chartW) * (st.zEnd - st.zStart + 1);
                let ns = st.zStart - dIdx;
                let ne = st.zEnd - dIdx;
                if (ns < 0) { ne -= ns; ns = 0; }
                if (ne > n - 1) { ns -= ne - (n - 1); ne = n - 1; }
                setZoom({ start: Math.max(0, ns), end: Math.min(n - 1, ne) });
            }
        }

        function onTouchEnd(e: TouchEvent) {
            if (e.touches.length === 0) touchRef.current = null;
            else if (e.touches.length < 2 && touchRef.current?.type === 'pinch') touchRef.current = null;
        }

        el.addEventListener('touchstart', onTouchStart, { passive: false });
        el.addEventListener('touchmove', onTouchMove, { passive: false });
        el.addEventListener('touchend', onTouchEnd);
        return () => {
            el.removeEventListener('touchstart', onTouchStart);
            el.removeEventListener('touchmove', onTouchMove);
            el.removeEventListener('touchend', onTouchEnd);
        };
    }, [zoomEnabled, marginLeft, chartW, zStart, zEnd, n]);

    // ── Mouse event handlers ─────────────────────────────────────────────────
    const getContainerXY = useCallback((clientX: number, clientY: number) => {
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return { cx: 0, cy: 0 };
        return { cx: clientX - rect.left, cy: clientY - rect.top };
    }, []);

    const handleMouseDown = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
        if (!zoomEnabled || !isZoomed) return;
        e.preventDefault();
        dragRef.current = { mouseX: e.clientX, zStart, zEnd };
        setIsDragging(true);
    }, [zoomEnabled, isZoomed, zStart, zEnd]);

    const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
        const svgRect = e.currentTarget.getBoundingClientRect();
        const svgX = e.clientX - svgRect.left;
        const { cx, cy } = getContainerXY(e.clientX, e.clientY);
        if (isDragging && dragRef.current) {
            const dx = e.clientX - dragRef.current.mouseX;
            const dIdx = (dx / chartW) * (zWindow + 1);
            let ns = dragRef.current.zStart - dIdx;
            let ne = dragRef.current.zEnd - dIdx;
            if (ns < 0) { ne -= ns; ns = 0; }
            if (ne > n - 1) { ns -= ne - (n - 1); ne = n - 1; }
            setZoom({ start: Math.max(0, ns), end: Math.min(n - 1, ne) });
            return;
        }
        if (svgX >= marginLeft && svgX <= marginLeft + chartW) {
            setTip({ visible: true, cx, cy, catIdx: svgXToIdx(svgX) });
        } else {
            setTip(p => ({ ...p, visible: false }));
        }
    }, [getContainerXY, isDragging, chartW, zWindow, n, marginLeft, svgXToIdx]);

    const handleMouseUp = useCallback(() => {
        if (isDragging) { setIsDragging(false); dragRef.current = null; }
    }, [isDragging]);

    const handleSvgLeave = useCallback(() => {
        setTip(p => ({ ...p, visible: false }));
        if (isDragging) { setIsDragging(false); dragRef.current = null; }
    }, [isDragging]);

    const handleDblClick = useCallback(() => {
        if (zoomEnabled && isZoomed) setZoom(null);
    }, [zoomEnabled, isZoomed]);

    // ── Theme tokens (memoized — only recompute when isDark changes) ───────────
    const themeTokens = useMemo(() => ({
        gridColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
        axisColor: isDark ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.09)',
        labelColor: isDark ? '#94a3b8' : '#64748b',
        btnBg: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
        btnColor: isDark ? '#94a3b8' : '#64748b',
        btnBorder: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)',
        tooltipHead: isDark ? '#e2e8f0' : '#1e293b',
        tooltipSub: isDark ? '#94a3b8' : '#64748b',
        dotStroke: isDark ? '#0d1520' : '#ffffff',
    }), [isDark]);
    const { gridColor, axisColor, labelColor, btnBg, btnColor, btnBorder,
        tooltipHead, tooltipSub, dotStroke } = themeTokens;

    // ──────────────────────────────────────────────────────────────────────────
    // RENDERERS
    // ──────────────────────────────────────────────────────────────────────────

    // ── Grid ─────────────────────────────────────────────────────────────────
    function renderGrid() {
        if (!showGrid) return null;
        return (
            <g>
                {ticks.map((t) => (
                    <line key={t}
                        x1={marginLeft} x2={marginLeft + chartW}
                        y1={yToSvg(t)} y2={yToSvg(t)}
                        stroke={gridColor} strokeWidth={1}
                        strokeDasharray={t === 0 ? undefined : '4 3'}
                    />
                ))}
                {/* Y axis rule */}
                <line x1={marginLeft} x2={marginLeft}
                    y1={MARGIN_TOP} y2={MARGIN_TOP + chartH}
                    stroke={axisColor} strokeWidth={1} />
                {/* X axis baseline */}
                <line x1={marginLeft} x2={marginLeft + chartW}
                    y1={MARGIN_TOP + chartH} y2={MARGIN_TOP + chartH}
                    stroke={axisColor} strokeWidth={1} />
            </g>
        );
    }

    // ── Y axis labels ────────────────────────────────────────────────────────
    function renderYAxis() {
        return (
            <g>
                {ticks.map((t) => (
                    <text key={t}
                        x={marginLeft - 8} y={yToSvg(t)}
                        textAnchor="end" dominantBaseline="central"
                        fontSize={10} fill={labelColor} fontFamily="inherit"
                    >
                        {yFmt(t)}
                    </text>
                ))}
            </g>
        );
    }

    // ── X axis labels ────────────────────────────────────────────────────────
    function renderXAxis() {
        const y = MARGIN_TOP + chartH + 14;
        return (
            <g>
                {xLabels.map((label, i) => {
                    if (i < viStart || i > viEnd) return null;
                    if (i % xLabelStep !== 0) return null;
                    const x = xCenter(i);
                    if (x < marginLeft - 4 || x > marginLeft + chartW + 4) return null;
                    const maxC = Math.max(3, Math.floor((xBandW * xLabelStep) / 6.4) - 1);
                    const trimmed = label.length > maxC ? `${label.slice(0, maxC - 1)}…` : label;
                    return (
                        <text key={i} x={x} y={y}
                            textAnchor="middle" dominantBaseline="hanging"
                            fontSize={10} fill={labelColor} fontFamily="inherit"
                        >
                            {trimmed}
                        </text>
                    );
                })}
            </g>
        );
    }

    // ── Crosshair ────────────────────────────────────────────────────────────
    function renderCrosshair() {
        if (!tip.visible || isPie || tip.catIdx < 0) return null;
        const x = xCenter(tip.catIdx);
        const bx = xToSvg(tip.catIdx);
        if (x < marginLeft - 1 || x > marginLeft + chartW + 1) return null;
        const bxC = Math.max(marginLeft, bx);
        const bwC = Math.min(xBandW, marginLeft + chartW - bxC);
        return (
            <g style={{ pointerEvents: 'none' }}>
                <rect x={bxC} y={MARGIN_TOP}
                    width={Math.max(0, bwC)} height={chartH}
                    fill={isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)'}
                />
                <line x1={x} x2={x} y1={MARGIN_TOP} y2={MARGIN_TOP + chartH}
                    stroke={isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'}
                    strokeWidth={1} strokeDasharray="4 3"
                />
            </g>
        );
    }

    // ── Zoom reset chip (shown only when zoomed; no +/− buttons — use pinch/wheel) ──
    function renderZoomControls() {
        if (!zoomEnabled || n <= 1 || !isZoomed) return null;
        return (
            <div style={{
                position: 'absolute', top: 4, right: MARGIN_RIGHT,
                display: 'flex', alignItems: 'center', gap: 3, zIndex: 10,
            }}>
                <button
                    onClick={() => setZoom(null)}
                    title="Reset zoom (double-click or double-tap)"
                    style={{
                        height: 20, borderRadius: 5, paddingInline: 6,
                        border: `1px solid ${btnBorder}`,
                        background: btnBg, color: btnColor,
                        cursor: 'pointer', fontSize: 10, lineHeight: '1',
                        display: 'flex', alignItems: 'center', gap: 3,
                        fontFamily: 'inherit', flexShrink: 0,
                    }}
                >
                    ↺
                    <span style={{ fontSize: 9, fontVariantNumeric: 'tabular-nums' }}>
                        {Math.round(zStart + 1)}–{Math.round(zEnd + 1)}/{n}
                    </span>
                </button>
            </div>
        );
    }

    // ── Bar series ────────────────────────────────────────────────────────────
    function renderBars() {
        // Cap bar width so very few data points don't produce enormous bars.
        // MAX_BAR_PX is the widest a single bar (not a group) should ever be.
        const MAX_BAR_PX = 72;
        const rawGroupW = Math.max(1, xBandW * (1 - barGroupPadding));
        const groupW = Math.max(1, Math.min(rawGroupW, MAX_BAR_PX * (isStacked ? 1 : series.length)));
        const groupPad = xBandW - groupW; // remainder is distributed as padding
        const barW = isStacked ? groupW : Math.max(1, groupW / series.length);

        // For stacked mode: only the topmost non-zero segment in each column gets rounded corners.
        // Segments below it must have flat tops so they connect seamlessly with the segment above.
        const topSeriesPerCol = topSeriesPerColMemo;

        return series.map((s, si) => {
            const color = resolvedColors[si];
            const barGradId = `bar-grad-${rawId}-${si}`;
            return (
                <g key={`bar-${si}`} clipPath={`url(#${clipId})`}>
                    {barGradient && (
                        <defs>
                            <linearGradient id={barGradId} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={color} stopOpacity={isDark ? 0.92 : 0.88} />
                                <stop offset="100%" stopColor={color} stopOpacity={isDark ? 0.62 : 0.72} />
                            </linearGradient>
                        </defs>
                    )}
                    {s.data.map((v, ci) => {
                        if (ci < viStart || ci > viEnd) return null;
                        if (typeof v !== 'number' || !Number.isFinite(v) || v <= 0) return null;

                        let base = 0;
                        if (isStacked) {
                            for (let k = 0; k < si; k++) base += (series[k].data[ci] ?? 0);
                        }
                        const topV = v + base;
                        const barH = Math.max(1, yToSvg(base) - yToSvg(topV));
                        const barTopY = yToSvg(topV);
                        const groupLeft = xToSvg(ci) + groupPad / 2;
                        const bx = isStacked ? groupLeft : groupLeft + si * barW;
                        const bw = Math.max(1, isStacked ? groupW : barW - 1);
                        // In stacked mode only the top-most segment per column gets rounded top corners.
                        // Middle/bottom segments must have flat tops to connect seamlessly with the one above.
                        const isTopSeg = !isStacked || topSeriesPerCol![ci] === si;
                        const rx = isTopSeg ? Math.min(barRadius, barH / 2, bw / 4) : 0;
                        const delay = animate ? ci * 0.012 + si * 0.025 : 0;
                        // Top-rounded-only path: arc on top-left and top-right, square bottom corners
                        const bRight = bx + bw;
                        const bBottom = barTopY + barH;
                        const barPath = rx > 0
                            ? `M ${bx + rx} ${barTopY} H ${bRight - rx} Q ${bRight} ${barTopY} ${bRight} ${barTopY + rx} V ${bBottom} H ${bx} V ${barTopY + rx} Q ${bx} ${barTopY} ${bx + rx} ${barTopY} Z`
                            : `M ${bx} ${barTopY} H ${bRight} V ${bBottom} H ${bx} Z`;

                        return (
                            <path key={`bar-${si}-${ci}`}
                                d={barPath}
                                fill={barGradient ? `url(#${barGradId})` : color}
                                style={{
                                    transformBox: 'fill-box',
                                    transformOrigin: '50% 100%',
                                    transform: `scaleY(${mounted ? 1 : 0})`,
                                    transition: animate
                                        ? `transform 0.48s cubic-bezier(0.34,1.3,0.64,1) ${delay}s`
                                        : 'none',
                                    cursor: onDataPointClick ? 'pointer' : 'default',
                                }}
                                onClick={onDataPointClick ? () => onDataPointClick(si, ci, v) : undefined}
                            />
                        );
                    })}
                    {/* Data labels above bars */}
                    {showDataLabels && s.data.map((v, ci) => {
                        if (ci < viStart || ci > viEnd) return null;
                        if (typeof v !== 'number' || !Number.isFinite(v) || v <= 0) return null;
                        let base = 0;
                        if (isStacked) for (let k = 0; k < si; k++) base += (series[k].data[ci] ?? 0);
                        const topV = v + base;
                        const groupLeft = xToSvg(ci) + (xBandW - groupW) / 2;
                        const bx = isStacked ? groupLeft : groupLeft + si * barW;
                        const bw = Math.max(1, isStacked ? groupW : barW - 1);
                        const cx = bx + bw / 2;
                        const lblY = yToSvg(topV) - 4;
                        return (
                            <text key={`dlbl-${si}-${ci}`} x={cx} y={lblY}
                                textAnchor="middle" dominantBaseline="auto"
                                fontSize={9} fontWeight={600} fill={color}
                                fontFamily="inherit" style={{ pointerEvents: 'none' }}
                            >{yFmt(v)}</text>
                        );
                    })}
                </g>
            );
        });
    }

    // ── Line series ───────────────────────────────────────────────────────────
    function renderLines() {
        return series.map((s, si) => {
            const color = resolvedColors[si];
            const pts: [number, number][] = [];
            const ptIdxMap: number[] = [];
            for (let i = viStart; i <= viEnd; i++) {
                const v = s.data[i];
                if (typeof v === 'number' && Number.isFinite(v)) {
                    pts.push([xCenter(i), yToSvg(v)]);
                    ptIdxMap.push(i);
                }
            }
            if (!pts.length) return null;
            const d = smoothBezier(pts, smooth);
            const resolvedDot = dotSize !== undefined ? dotSize : strokeWidth + 1;
            const showDot = resolvedDot > 0 && xBandW > 26;
            const delay = animate ? si * 0.1 : 0;
            return (
                <g key={`line-${si}`} clipPath={`url(#${clipId})`}
                    style={{
                        opacity: mounted ? 1 : 0,
                        transition: animate ? `opacity 0.5s ease ${delay}s` : 'none',
                    }}
                >
                    <path d={d} stroke={color} strokeWidth={strokeWidth}
                        fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    {showDot && pts.map(([px, py], i) => (
                        <circle key={i} cx={px} cy={py} r={resolvedDot}
                            fill={color} stroke={dotStroke} strokeWidth={1.2}
                            style={{ cursor: onDataPointClick ? 'pointer' : 'default' }}
                            onClick={onDataPointClick ? () => onDataPointClick(si, ptIdxMap[i], s.data[ptIdxMap[i]] ?? 0) : undefined}
                        />
                    ))}
                    {showDataLabels && pts.map(([px, py], i) => {
                        const val = s.data[ptIdxMap[i]];
                        if (val === undefined) return null;
                        return (
                            <text key={`dlbl-${i}`} x={px} y={py - 8}
                                textAnchor="middle" dominantBaseline="auto"
                                fontSize={9} fontWeight={600} fill={color}
                                fontFamily="inherit" style={{ pointerEvents: 'none' }}
                            >{yFmt(val)}</text>
                        );
                    })}
                </g>
            );
        });
    }

    // ── Area series ───────────────────────────────────────────────────────────
    function renderAreas() {
        const bY = yToSvg(0);
        return series.map((s, si) => {
            const color = resolvedColors[si];
            const gradId = `area-grad-${rawId}-${si}`;
            const pts: [number, number][] = [];
            const ptIdxMap: number[] = [];
            for (let i = viStart; i <= viEnd; i++) {
                const v = s.data[i];
                if (typeof v === 'number' && Number.isFinite(v)) {
                    pts.push([xCenter(i), yToSvg(v)]);
                    ptIdxMap.push(i);
                }
            }
            if (pts.length < 2) {
                // Fall back to a single dot when only one visible data point
                if (pts.length === 1) {
                    const [px, py] = pts[0];
                    return (
                        <g key={`area-${si}`} clipPath={`url(#${clipId})`}
                            style={{
                                opacity: mounted ? 1 : 0,
                                transition: animate ? `opacity 0.55s ease ${si * 0.1}s` : 'none',
                            }}
                        >
                            <circle cx={px} cy={py} r={strokeWidth + 2}
                                fill={color} stroke={dotStroke} strokeWidth={1.5} />
                        </g>
                    );
                }
                return null;
            }
            const lp = smoothBezier(pts, smooth);
            const ap = `${lp} L${pts[pts.length - 1][0]},${bY} L${pts[0][0]},${bY} Z`;
            const delay = animate ? si * 0.1 : 0;
            const resolvedDot = dotSize !== undefined ? dotSize : strokeWidth + 1;
            const showDot = resolvedDot > 0 && xBandW > 26;
            return (
                <g key={`area-${si}`} clipPath={`url(#${clipId})`}
                    style={{
                        opacity: mounted ? 1 : 0,
                        transition: animate ? `opacity 0.55s ease ${delay}s` : 'none',
                    }}
                >
                    <defs>
                        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={color} stopOpacity={isDark ? Math.min(fillOpacity * 1.4, 0.32) : fillOpacity} />
                            <stop offset="60%" stopColor={color} stopOpacity={fillOpacity * 0.12} />
                            <stop offset="100%" stopColor={color} stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <path d={ap} fill={`url(#${gradId})`} />
                    <path d={lp} stroke={color} strokeWidth={strokeWidth}
                        fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    {showDot && pts.map(([px, py], i) => (
                        <circle key={i} cx={px} cy={py} r={resolvedDot}
                            fill={color} stroke={dotStroke} strokeWidth={1.2}
                            style={{ cursor: onDataPointClick ? 'pointer' : 'default' }}
                            onClick={onDataPointClick ? () => onDataPointClick(si, ptIdxMap[i], s.data[ptIdxMap[i]] ?? 0) : undefined}
                        />
                    ))}
                    {showDataLabels && pts.map(([px, py], i) => {
                        const val = s.data[ptIdxMap[i]];
                        if (val === undefined) return null;
                        return (
                            <text key={`dlbl-${i}`} x={px} y={py - 8}
                                textAnchor="middle" dominantBaseline="auto"
                                fontSize={9} fontWeight={600} fill={color}
                                fontFamily="inherit" style={{ pointerEvents: 'none' }}
                            >{yFmt(val)}</text>
                        );
                    })}
                </g>
            );
        });
    }

    // ── Reference lines ───────────────────────────────────────────────────────
    function renderReferenceLines() {
        if (!referenceLines?.length) return null;
        return (
            <g style={{ pointerEvents: 'none' }}>
                {referenceLines.map((rl, ri) => {
                    const sy = yToSvg(rl.y);
                    if (sy < MARGIN_TOP || sy > MARGIN_TOP + chartH) return null;
                    const lineColor = rl.color ?? resolvedColors[0] ?? '#888';
                    return (
                        <g key={ri}>
                            <line
                                x1={marginLeft} x2={marginLeft + chartW}
                                y1={sy} y2={sy}
                                stroke={lineColor} strokeWidth={1.5}
                                strokeDasharray={rl.dash ?? '6 3'}
                                opacity={0.8}
                            />
                            {rl.label && (
                                <text
                                    x={marginLeft + chartW - 4} y={sy - 4}
                                    textAnchor="end" dominantBaseline="auto"
                                    fontSize={9} fontWeight={600}
                                    fill={lineColor} fontFamily="inherit"
                                    opacity={0.9}
                                >
                                    {rl.label}
                                </text>
                            )}
                        </g>
                    );
                })}
            </g>
        );
    }

    // ── Pie / Donut ───────────────────────────────────────────────────────────
    function renderPieDonut() {
        const cx = containerW / 2;
        const cy = height / 2;
        const minDim = Math.min(containerW, height);
        const outerR = minDim * 0.4;
        const innerR = type === 'donut' ? outerR * donutInnerRatio : 0;
        const sliceData = series[0]?.data ?? [];
        const total = sliceData.reduce((a, b) => a + b, 0) || 1;
        const GAP_RAD = sliceData.length > 1 ? 0.025 : 0;

        let angle = -Math.PI / 2;
        return (
            <svg width={containerW} height={height} style={{ display: 'block' }}>
                {sliceData.map((v, i) => {
                    const sliceAngle = (v / total) * 2 * Math.PI;
                    const startA = angle + GAP_RAD;
                    const endA = angle + sliceAngle - GAP_RAD;
                    const d = arcPath(cx, cy, outerR, innerR, startA, endA);
                    angle += sliceAngle;

                    const midA = startA + (endA - startA) / 2;
                    const midR = innerR > 0 ? (innerR + outerR) / 2 : outerR * 0.65;
                    const lx = cx + midR * Math.cos(midA);
                    const ly = cy + midR * Math.sin(midA);
                    const pct = ((v / total) * 100).toFixed(0);
                    const color = resolvedColors[i % resolvedColors.length];
                    const delay = animate ? i * 0.06 : 0;

                    return (
                        <g key={`slice-${i}`}>
                            <path
                                d={d}
                                fill={color}
                                style={{
                                    opacity: mounted ? 1 : 0,
                                    transform: mounted ? 'scale(1)' : 'scale(0.85)',
                                    transformOrigin: `${cx}px ${cy}px`,
                                    transformBox: 'view-box',
                                    transition: animate
                                        ? `opacity 0.45s ease ${delay}s, transform 0.45s cubic-bezier(0.34,1.3,0.64,1) ${delay}s`
                                        : 'none',
                                    cursor: 'default',
                                }}
                                onMouseEnter={(e) => {
                                    const svgRect = (e.currentTarget.ownerSVGElement as SVGSVGElement)?.getBoundingClientRect();
                                    const contRect = containerRef.current?.getBoundingClientRect();
                                    if (svgRect && contRect) {
                                        setTip({
                                            visible: true,
                                            cx: e.clientX - contRect.left,
                                            cy: e.clientY - contRect.top,
                                            catIdx: i,
                                            pieSlice: i,
                                        });
                                    }
                                }}
                                onMouseLeave={handleSvgLeave}
                            />
                            {/* Percentage label inside slice */}
                            {sliceAngle > 0.35 && (
                                <text
                                    x={lx}
                                    y={ly}
                                    textAnchor="middle"
                                    dominantBaseline="central"
                                    fontSize={11}
                                    fontWeight={700}
                                    fill="rgba(255,255,255,0.92)"
                                    style={{ pointerEvents: 'none', userSelect: 'none' }}
                                >
                                    {pct}%
                                </text>
                            )}
                        </g>
                    );
                })}
                {/* Donut center label */}
                {type === 'donut' && (
                    <text
                        x={cx}
                        y={cy}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={22}
                        fontWeight={700}
                        fill={isDark ? '#f1f5f9' : '#1e293b'}
                        style={{ userSelect: 'none' }}
                    >
                        {donutLabel ?? fmtNum(sliceData.reduce((a, b) => a + b, 0))}
                    </text>
                )}
            </svg>
        );
    }

    // ── Empty state ───────────────────────────────────────────────────────────
    if (!series.length || !hasData) {
        return (
            <div
                ref={containerRef}
                className={className}
                style={{
                    height,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 10,
                }}
            >
                <svg width={44} height={36} viewBox="0 0 44 36" fill="none">
                    <rect
                        x={2}
                        y={20}
                        width={10}
                        height={14}
                        rx={2}
                        fill={isDark ? '#1e293b' : '#e2e8f0'}
                    />
                    <rect
                        x={17}
                        y={12}
                        width={10}
                        height={22}
                        rx={2}
                        fill={isDark ? '#1e293b' : '#e2e8f0'}
                    />
                    <rect
                        x={32}
                        y={4}
                        width={10}
                        height={30}
                        rx={2}
                        fill={isDark ? '#1e293b' : '#e2e8f0'}
                    />
                </svg>
                <span
                    style={{
                        fontSize: 12,
                        color: isDark ? '#475569' : '#94a3b8',
                        fontFamily: 'inherit',
                    }}
                >
                    {noDataLabel}
                </span>
            </div>
        );
    }

    // ── Main render ───────────────────────────────────────────────────────────
    const svgCursor = isZoomed
        ? (isDragging ? 'grabbing' : 'grab')
        : 'crosshair';

    const cartesianSvg = !isPie && (
        <svg
            ref={svgRef}
            width={svgW}
            height={svgH}
            style={{ display: 'block', overflow: 'hidden', cursor: svgCursor, userSelect: 'none' }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleSvgLeave}
            onDoubleClick={handleDblClick}
        >
            <defs>
                <clipPath id={clipId}>
                    <rect x={marginLeft} y={MARGIN_TOP} width={chartW} height={chartH + 1} />
                </clipPath>
            </defs>
            {renderGrid()}
            {renderYAxis()}
            {renderXAxis()}
            <g clipPath={`url(#${clipId})`}>
                {renderCrosshair()}
            </g>
            {(type === 'bar' || type === 'stacked' || isStacked) && renderBars()}
            {type === 'line' && renderLines()}
            {type === 'area' && renderAreas()}
            {renderReferenceLines()}
        </svg>
    );

    const isLineOrArea = type === 'line' || type === 'area';

    const chartArea = (
        <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
            {isPie ? renderPieDonut() : cartesianSvg}
            <GtChartTooltip
                visible={tip.visible && hasData && tooltipRows.length > 0}
                cx={tip.cx}
                cy={tip.cy}
                catLabel={tooltipCatLabel}
                rows={tooltipRows}
                isDark={isDark}
                containerW={containerW}
                height={height}
                tooltipHead={tooltipHead}
                tooltipSub={tooltipSub}
                formatTooltipValue={formatTooltipValue}
            />
            {renderZoomControls()}
        </div>
    );

    if (legRight) {
        return (
            <div
                ref={containerRef}
                className={className}
                style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center' }}
            >
                {chartArea}
                <GtChartLegend items={legendItems} pos="right" isDark={isDark} labelColor={labelColor} isLineOrArea={isLineOrArea} />
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className={className}
            style={{ position: 'relative', width: '100%' }}
        >
            {legTop && <GtChartLegend items={legendItems} pos="top" isDark={isDark} labelColor={labelColor} isLineOrArea={isLineOrArea} />}
            {chartArea}
            {legBottom && <GtChartLegend items={legendItems} pos="bottom" isDark={isDark} labelColor={labelColor} isLineOrArea={isLineOrArea} />}
        </div>
    );
};

export default GtChart;
